import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useLatest } from 'ahooks';
import { ChangeContext } from 'tea-component';
import GPBaseSelectMultiple from '@gp/components/SelectMultiple/BaseSelectMultiple';
import useSearch from '@gp/components/Select/_hooks/useSearch';
import useSelectMultiple from '@gp/components/SelectMultiple/_hooks/useSelectMultiple';
import { defaultSelectFilter } from '@gp/utils/common';
import { formatOptions } from '@gp/components/Select/utils';
import { defaultOptionTextRenderer } from '@gp/components/Select/utils/renderers';
import { getDividedOptions } from '@gp/components/SelectMultiple/utils';
import { renderButton } from '@gp/components/SelectMultiple/utils/renderers';
import { ALL_OPTION, GROUPS } from '@gp/constants/select';
import useSearchSelectInherit from './_hooks/useSearchSelectInherit';

/**
 * GP SearchableSelectMultiple for strategic, with search selection inheritance.
 * Use instead of @gp/components/SelectMultiple when searchable + keep-checked-on-search is required.
 */
const SearchableSelectMultipleInherit: React.FC<IGPSearchableSelectMultipleProps> = (props) => {
  const {
    className,
    overlayClassName,
    boxClassName,
    showLabel = true,
    label = 'Metrics : ',
    required = true,
    sortable = true,
    staging = false,
    searchable,
    searchPlaceholder = 'Search',
    filter = defaultSelectFilter,
    searchValue: searchValueProps = '',
    onSearchValueChange: onSearchValueChangeProps,
    onSearch: onSearchProps,
    allOption = ALL_OPTION,
    groups = GROUPS,
    button: buttonProps,
    optionTextRenderer = defaultOptionTextRenderer,
    options: optionsProps,
    value: valueProps = [],
    onChange,
    onOpen,
    onClose,
    ...restProps
  } = props;

  const optionTextRendererLatest = useLatest<IGPSelectOptionTextRenderer>(optionTextRenderer);

  const { searchMode, resetSearchMode, searchValue, onSearchValueChange, onSearch } = useSearch({
    searchable: true,
    searchValue: searchValueProps,
    onSearchValueChange: onSearchValueChangeProps,
    onSearch: onSearchProps,
  });

  const options = useMemo<IGPSelectOptionWithGroup[]>(() => {
    return formatOptions(optionsProps, { optionTextRenderer: optionTextRendererLatest.current });
  }, [optionsProps]);

  const {
    dividedOptions,
    setDividedOptions,
    value,
    setValue,
    onChange: handleChange,
    onOpen: handleOpen,
    onClose: handleClose,
  } = useSelectMultiple({
    required,
    sortable,
    staging,
    options,
    value: valueProps,
    onChange,
    onChangePreProcess: (payload, context) => {
      if (searchMode) {
        onChangeWhenSearching(payload, context);
        return false;
      }
      return true;
    },
    onOpen,
    onClose: () => {
      resetSearchMode();
      onClose?.();
    },
  });

  const { searchingGroups, searchingOptions, searchingValue, onChangeWhenSearching } = useSearchSelectInherit({
    searchMode,
    searchValue,
    filter,
    options,
    optionTextRenderer,
    value,
    setValue,
  });

  const button = renderButton(buttonProps, {
    allOption,
    options,
    value,
    showLabel,
    label,
    optionTextRenderer,
  });

  const handleSearch = (payload?: string, context?: ChangeContext<React.SyntheticEvent<Element, Event>>) => {
    if (!payload) {
      const optionsTmp = formatOptions(optionsProps, { optionTextRenderer });
      const dividedOptionsTmp = getDividedOptions(optionsTmp, value);
      setDividedOptions(dividedOptionsTmp);
    }
    onSearch(payload, context);
  };

  return (
    <GPBaseSelectMultiple
      {...restProps}
      className={classnames('dbg-common-select-multiple--searchable', className)}
      overlayClassName={classnames('dbg-common-select-multiple-overlay--searchable', overlayClassName, {
        'dbg-common-select-multiple-overlay--all-option': !searchMode && !!allOption,
        'dbg-common-select-multiple-overlay--searching': searchMode,
        'dbg-common-select-multiple-overlay--searching-empty': searchMode && !searchingOptions?.length,
      })}
      boxClassName={classnames('dbg-common-select-multiple-box--searchable', boxClassName)}
      showLabel={showLabel}
      label={label}
      staging={staging}
      searchable={true}
      searchPlaceholder={searchPlaceholder}
      filter={filter}
      searchValue={searchValue}
      onSearchValueChange={onSearchValueChange}
      onSearch={handleSearch}
      allOption={searchMode ? false : allOption}
      groups={searchMode ? searchingGroups : groups}
      button={button}
      options={searchMode ? searchingOptions : dividedOptions}
      value={searchMode ? searchingValue : value}
      onChange={handleChange}
      onOpen={handleOpen}
      onClose={handleClose}
    />
  );
};

export default SearchableSelectMultipleInherit;
